import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { API } from '../App';
import axios from 'axios';
import { 
  Landmark, Users, UserCheck, CreditCard, PlusCircle, Eye, Trash2, 
  ChevronLeft, DollarSign, Calendar, FileText, AlertTriangle,
  Building2, Search, X, Check, Loader2, BarChart3, Download,
  Bell, Send, ChevronDown, ChevronUp, Briefcase, Mail, BookOpen
} from 'lucide-react';
import BlogAdmin from '../components/BlogAdmin';

const ax = axios.create({ withCredentials: true });

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);

// ========================= DASHBOARD VIEW =========================
const DashboardView = ({ metrics, onNavigate }) => (
  <div data-testid="prestamos-dashboard">
    <h2 className="text-2xl font-bold mb-6 text-foreground">Panel de Control</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        { label: 'Acreedores Activos', value: metrics.acreedores_activos, icon: UserCheck, color: 'text-blue-500' },
        { label: 'Préstamos Totales', value: fmt(metrics.prestamos_totales), icon: Landmark, color: 'text-emerald-500' },
        { label: 'Abonos del Mes', value: fmt(metrics.abonos_mes), icon: DollarSign, color: 'text-amber-500' },
        { label: 'Socios Activos', value: metrics.socios_activos, icon: Users, color: 'text-purple-500' },
      ].map((card, i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-shadow" data-testid={`metric-card-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <card.icon className={`${card.color}`} size={24} />
          </div>
          <div className="text-2xl font-bold text-foreground">{card.value}</div>
          <div className="text-sm text-muted-foreground mt-1">{card.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// ========================= SOCIOS VIEW =========================
const SociosView = ({ onNavigate }) => {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSocio, setEditingSocio] = useState(null);
  const [form, setForm] = useState({ nombre_completo: '', documento: '', telefono: '', email: '', direccion: '', banco: '', tipo_cuenta: 'ahorros', nro_cuenta: '', aporte: 0, porcentaje_participacion: 0, observaciones: '', estado: 'activo' });

  const load = useCallback(async () => {
    try { const r = await ax.get(`${API}/prestamos/socios`); setSocios(r.data); } catch(e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editingSocio) {
        await ax.put(`${API}/prestamos/socios/${editingSocio.id}`, form);
      } else {
        await ax.post(`${API}/prestamos/socios`, form);
      }
      setShowForm(false); setEditingSocio(null);
      setForm({ nombre_completo: '', documento: '', telefono: '', email: '', direccion: '', banco: '', tipo_cuenta: 'ahorros', nro_cuenta: '', aporte: 0, porcentaje_participacion: 0, observaciones: '', estado: 'activo' });
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este socio?')) return;
    try { await ax.delete(`${API}/prestamos/socios/${id}`); load(); } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const openEdit = (s) => { setEditingSocio(s); setForm(s); setShowForm(true); };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="socios-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Socios</h2>
        <button onClick={() => { setEditingSocio(null); setForm({ nombre_completo: '', documento: '', telefono: '', email: '', direccion: '', banco: '', tipo_cuenta: 'ahorros', nro_cuenta: '', aporte: 0, porcentaje_participacion: 0, observaciones: '', estado: 'activo' }); setShowForm(true); }} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e] transition-colors" data-testid="add-socio-btn">
          <PlusCircle size={18} /> Nuevo Socio
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="socio-form">
          <h3 className="text-lg font-bold mb-4 text-foreground">{editingSocio ? 'Editar Socio' : 'Nuevo Socio'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'nombre_completo', label: 'Nombre Completo *', type: 'text' },
              { key: 'documento', label: 'Documento *', type: 'text' },
              { key: 'telefono', label: 'Teléfono', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'direccion', label: 'Dirección', type: 'text' },
              { key: 'banco', label: 'Banco', type: 'text' },
              { key: 'nro_cuenta', label: 'Nro. Cuenta', type: 'text' },
              { key: 'aporte', label: 'Aporte', type: 'number' },
              { key: 'porcentaje_participacion', label: '% Participación', type: 'number' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-sm text-muted-foreground mb-1 block">{f.label}</label>
                <input type={f.type} value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid={`socio-${f.key}`} />
              </div>
            ))}
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Tipo Cuenta</label>
              <select value={form.tipo_cuenta} onChange={e => setForm({...form, tipo_cuenta: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="ahorros">Ahorros</option>
                <option value="corriente">Corriente</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Estado</label>
              <select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-muted-foreground mb-1 block">Observaciones</label>
              <textarea value={form.observaciones || ''} onChange={e => setForm({...form, observaciones: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" rows={2} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="bg-[#D4AF37] text-black px-6 py-2 rounded-lg font-medium hover:bg-[#c29d2e]" data-testid="save-socio-btn">Guardar</button>
            <button onClick={() => setShowForm(false)} className="bg-muted text-muted-foreground px-6 py-2 rounded-lg">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['Nombre', 'Documento', 'Teléfono', 'Banco', 'Aporte', '% Part.', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {socios.map(s => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`socio-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-foreground">{s.nombre_completo}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.documento}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.telefono || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.banco || '-'}</td>
                  <td className="px-4 py-3 text-foreground">{fmt(s.aporte)}</td>
                  <td className="px-4 py-3 text-foreground">{s.porcentaje_participacion}%</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{s.estado}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(s)} className="text-blue-400 hover:text-blue-300" data-testid={`edit-socio-${s.id}`}><FileText size={16} /></button>
                      <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-socio-${s.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {socios.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No hay socios registrados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========================= DEUDORES VIEW =========================
const DeudoresView = ({ onNavigate }) => {
  const [deudores, setDeudores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDeudor, setEditingDeudor] = useState(null);
  const [form, setForm] = useState({ nombre_completo: '', documento: '', telefono: '', direccion: '', email: '', observaciones: '', estado: 'activo' });

  const load = useCallback(async () => {
    try { const r = await ax.get(`${API}/prestamos/deudores`); setDeudores(r.data); } catch(e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editingDeudor) {
        await ax.put(`${API}/prestamos/deudores/${editingDeudor.id}`, form);
      } else {
        await ax.post(`${API}/prestamos/deudores`, form);
      }
      setShowForm(false); setEditingDeudor(null);
      setForm({ nombre_completo: '', documento: '', telefono: '', direccion: '', email: '', observaciones: '', estado: 'activo' });
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este deudor?')) return;
    try { await ax.delete(`${API}/prestamos/deudores/${id}`); load(); } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="deudores-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Deudores</h2>
        <button onClick={() => { setEditingDeudor(null); setForm({ nombre_completo: '', documento: '', telefono: '', direccion: '', email: '', observaciones: '', estado: 'activo' }); setShowForm(true); }} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e]" data-testid="add-deudor-btn">
          <PlusCircle size={18} /> Nuevo Deudor
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="deudor-form">
          <h3 className="text-lg font-bold mb-4 text-foreground">{editingDeudor ? 'Editar Deudor' : 'Nuevo Deudor'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'nombre_completo', label: 'Nombre Completo *' },
              { key: 'documento', label: 'Documento *' },
              { key: 'telefono', label: 'Teléfono' },
              { key: 'email', label: 'Email' },
              { key: 'direccion', label: 'Dirección' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-sm text-muted-foreground mb-1 block">{f.label}</label>
                <input type="text" value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid={`deudor-${f.key}`} />
              </div>
            ))}
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Estado</label>
              <select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-muted-foreground mb-1 block">Observaciones</label>
              <textarea value={form.observaciones || ''} onChange={e => setForm({...form, observaciones: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" rows={2} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="bg-[#D4AF37] text-black px-6 py-2 rounded-lg font-medium hover:bg-[#c29d2e]" data-testid="save-deudor-btn">Guardar</button>
            <button onClick={() => setShowForm(false)} className="bg-muted text-muted-foreground px-6 py-2 rounded-lg">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['Nombre', 'Documento', 'Teléfono', 'Email', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deudores.map(d => (
                <tr key={d.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`deudor-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium text-foreground">{d.nombre_completo}</td>
                  <td className="px-4 py-3 text-muted-foreground">{d.documento}</td>
                  <td className="px-4 py-3 text-muted-foreground">{d.telefono || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{d.email || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{d.estado}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingDeudor(d); setForm(d); setShowForm(true); }} className="text-blue-400 hover:text-blue-300"><FileText size={16} /></button>
                      <button onClick={() => handleDelete(d.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {deudores.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No hay deudores registrados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========================= PRESTAMOS LIST VIEW =========================
const PrestamosListView = ({ onNavigate }) => {
  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('activo');

  const load = useCallback(async () => {
    try { const r = await ax.get(`${API}/prestamos/list?estado=${filtroEstado}`); setPrestamos(r.data); } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [filtroEstado]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="prestamos-list-view">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <h2 className="text-2xl font-bold text-foreground">Préstamos</h2>
        <div className="flex gap-3">
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="filter-estado">
            <option value="todos">Todos</option>
            <option value="activo">Activos</option>
            <option value="cancelado">Cancelados</option>
          </select>
          <button onClick={() => onNavigate('nuevo-prestamo')} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e]" data-testid="add-prestamo-btn">
            <PlusCircle size={18} /> Nuevo Préstamo
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['Consecutivo', 'Deudor', 'Monto', 'Tasa', 'Plazo', 'Periodo', 'Fecha Inicio', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prestamos.map(p => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`prestamo-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono font-medium text-foreground">{p.consecutivo}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{p.deudor_nombre || '-'}</td>
                  <td className="px-4 py-3 text-foreground">{fmt(p.monto)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.tasa_interes}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.plazo}</td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{p.periodo_pago}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.fecha_inicio}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : p.estado === 'cancelado' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{p.estado}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => onNavigate('detalle-prestamo', p.id)} className="text-blue-400 hover:text-blue-300" data-testid={`view-prestamo-${p.id}`}><Eye size={16} /></button>
                  </td>
                </tr>
              ))}
              {prestamos.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No hay préstamos registrados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========================= NEW PRESTAMO FORM =========================
const NuevoPrestamoForm = ({ onNavigate }) => {
  const [deudores, setDeudores] = useState([]);
  const [socios, setSocios] = useState([]);
  const [form, setForm] = useState({ deudor_id: '', monto: '', tasa_interes: '', plazo: '', tipo_tasa: 'mensual', periodo_pago: 'mensual', fecha_inicio: '', observaciones: '' });
  const [selectedSocios, setSelectedSocios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    ax.get(`${API}/prestamos/deudores`).then(r => setDeudores(r.data));
    ax.get(`${API}/prestamos/socios`).then(r => setSocios(r.data.filter(s => s.estado === 'activo')));
  }, []);

  const addSocio = () => setSelectedSocios([...selectedSocios, { socio_id: '', aporte: 0, porcentaje_interes: 0 }]);
  const removeSocio = (i) => setSelectedSocios(selectedSocios.filter((_, idx) => idx !== i));
  const updateSocio = (i, field, value) => {
    const updated = [...selectedSocios];
    updated[i] = { ...updated[i], [field]: value };
    setSelectedSocios(updated);
  };

  const handleSubmit = async () => {
    if (!form.deudor_id || !form.monto || !form.tasa_interes || !form.plazo || !form.fecha_inicio) {
      alert('Complete todos los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        monto: parseFloat(form.monto),
        tasa_interes: parseFloat(form.tasa_interes),
        plazo: parseInt(form.plazo),
        socios: selectedSocios.filter(s => s.socio_id).map(s => ({ ...s, aporte: parseFloat(s.aporte), porcentaje_interes: parseFloat(s.porcentaje_interes || 0) }))
      };
      await ax.post(`${API}/prestamos/create`, payload);
      onNavigate('prestamos');
    } catch(e) { alert(e.response?.data?.detail || 'Error al crear préstamo'); } finally { setSaving(false); }
  };

  return (
    <div data-testid="nuevo-prestamo-form">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => onNavigate('prestamos')} className="text-muted-foreground hover:text-foreground"><ChevronLeft size={24} /></button>
        <h2 className="text-2xl font-bold text-foreground">Nuevo Préstamo</h2>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Deudor *</label>
            <select value={form.deudor_id} onChange={e => setForm({...form, deudor_id: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="select-deudor">
              <option value="">Seleccionar deudor...</option>
              {deudores.map(d => <option key={d.id} value={d.id}>{d.nombre_completo} - {d.documento}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Monto *</label>
            <input type="number" value={form.monto} onChange={e => setForm({...form, monto: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="input-monto" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Tasa de Interés (%) *</label>
            <input type="number" step="0.01" value={form.tasa_interes} onChange={e => setForm({...form, tasa_interes: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="input-tasa" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Plazo (cuotas) *</label>
            <input type="number" value={form.plazo} onChange={e => setForm({...form, plazo: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="input-plazo" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Periodo de Pago</label>
            <select value={form.periodo_pago} onChange={e => setForm({...form, periodo_pago: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
              <option value="diario">Diario</option>
              <option value="semanal">Semanal</option>
              <option value="quincenal">Quincenal</option>
              <option value="mensual">Mensual</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Fecha de Inicio *</label>
            <input type="date" value={form.fecha_inicio} onChange={e => setForm({...form, fecha_inicio: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="input-fecha" />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="text-sm text-muted-foreground mb-1 block">Observaciones</label>
            <textarea value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" rows={2} />
          </div>
        </div>

        {/* Socios section */}
        <div className="border-t border-border pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-foreground">Socios (Aportes)</h3>
            <button onClick={addSocio} className="flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#c29d2e]" data-testid="add-socio-to-prestamo">
              <PlusCircle size={16} /> Agregar Socio
            </button>
          </div>
          {selectedSocios.map((ss, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 items-end">
              <div>
                <label className="text-xs text-muted-foreground">Socio</label>
                <select value={ss.socio_id} onChange={e => updateSocio(i, 'socio_id', e.target.value)} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                  <option value="">Seleccionar...</option>
                  {socios.map(s => <option key={s.id} value={s.id}>{s.nombre_completo}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Aporte ($)</label>
                <input type="number" value={ss.aporte} onChange={e => updateSocio(i, 'aporte', e.target.value)} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">% Interés Socio</label>
                <input type="number" step="0.01" value={ss.porcentaje_interes} onChange={e => updateSocio(i, 'porcentaje_interes', e.target.value)} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
              </div>
              <button onClick={() => removeSocio(i)} className="text-red-400 hover:text-red-300 pb-2"><X size={18} /></button>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={handleSubmit} disabled={saving} className="bg-[#D4AF37] text-black px-8 py-2.5 rounded-lg font-medium hover:bg-[#c29d2e] disabled:opacity-50 flex items-center gap-2" data-testid="submit-prestamo-btn">
            {saving && <Loader2 className="animate-spin" size={16} />} Crear Préstamo
          </button>
          <button onClick={() => onNavigate('prestamos')} className="bg-muted text-muted-foreground px-6 py-2.5 rounded-lg">Cancelar</button>
        </div>
      </div>
    </div>
  );
};

// ========================= PRESTAMO DETAIL VIEW =========================
const PrestamoDetailView = ({ prestamoId, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagoTipo, setPagoTipo] = useState('cuota');
  const [selectedCuotas, setSelectedCuotas] = useState([]);
  const [pagando, setPagando] = useState(false);
  const [metodoPago, setMetodoPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [montoAbono, setMontoAbono] = useState('');

  const load = useCallback(async () => {
    try { const r = await ax.get(`${API}/prestamos/detail/${prestamoId}`); setData(r.data); } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [prestamoId]);

  useEffect(() => { load(); }, [load]);

  const toggleCuota = (id) => {
    setSelectedCuotas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handlePago = async () => {
    setPagando(true);
    try {
      await ax.post(`${API}/prestamos/pagos`, {
        prestamo_id: prestamoId,
        tipo: pagoTipo,
        cuotas: selectedCuotas,
        metodo_pago: metodoPago,
        referencia: referencia,
        monto_abono: pagoTipo === 'abono_capital' ? parseFloat(montoAbono) || 0 : undefined
      });
      setShowPagoModal(false);
      setSelectedCuotas([]);
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error al procesar pago'); } finally { setPagando(false); }
  };

  const handleEliminar = async () => {
    if (!window.confirm('¿Está seguro de eliminar este préstamo?')) return;
    try { await ax.delete(`${API}/prestamos/${prestamoId}`); onNavigate('prestamos'); } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">Préstamo no encontrado</div>;

  const pendientes = (data.detalles || []).filter(d => d.estado !== 'pagado' && d.estado !== 'solo_interes');

  return (
    <div data-testid="prestamo-detail-view">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => onNavigate('prestamos')} className="text-muted-foreground hover:text-foreground"><ChevronLeft size={24} /></button>
        <h2 className="text-2xl font-bold text-foreground">Préstamo {data.consecutivo}</h2>
        <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${data.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{data.estado}</span>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Deudor</div>
          <div className="text-lg font-bold text-foreground">{data.deudor_nombre}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Monto</div>
          <div className="text-lg font-bold text-foreground">{fmt(data.monto)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Tasa / Periodo</div>
          <div className="text-lg font-bold text-foreground">{data.tasa_interes}% / {data.periodo_pago}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-sm text-muted-foreground">Fecha Inicio</div>
          <div className="text-lg font-bold text-foreground">{data.fecha_inicio}</div>
        </div>
      </div>

      {/* Actions */}
      {data.estado === 'activo' && (
        <div className="flex gap-3 mb-6">
          <button onClick={() => setShowPagoModal(true)} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-500" data-testid="registrar-pago-btn">
            <CreditCard size={18} /> Registrar Pago
          </button>
          <button onClick={handleEliminar} className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-500" data-testid="eliminar-prestamo-btn">
            <Trash2 size={18} /> Eliminar
          </button>
        </div>
      )}

      {/* Amortization Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <h3 className="font-bold text-foreground">Tabla de Amortización</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['#', 'Fecha', 'Capital', 'Interés', 'Total Cuota', 'Pagado Cap.', 'Pagado Int.', 'Saldo', 'Estado'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.detalles || []).map(d => (
                <tr key={d.id} className={`border-t border-border ${d.estado === 'pagado' ? 'bg-emerald-500/5' : d.estado === 'solo_interes' ? 'bg-yellow-500/5' : 'hover:bg-muted/30'}`} data-testid={`cuota-row-${d.numero_cuota}`}>
                  <td className="px-3 py-2 text-foreground">{d.numero_cuota}</td>
                  <td className="px-3 py-2 text-muted-foreground">{d.fecha_programada}</td>
                  <td className="px-3 py-2 text-foreground">{fmt(d.capital)}</td>
                  <td className="px-3 py-2 text-foreground">{fmt(d.interes)}</td>
                  <td className="px-3 py-2 font-medium text-foreground">{fmt(d.total_cuota)}</td>
                  <td className="px-3 py-2 text-emerald-400">{fmt(d.pagado_capital)}</td>
                  <td className="px-3 py-2 text-emerald-400">{fmt(d.pagado_interes)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmt(d.saldo_restante)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      d.estado === 'pagado' ? 'bg-emerald-500/20 text-emerald-400' :
                      d.estado === 'parcial' ? 'bg-yellow-500/20 text-yellow-400' :
                      d.estado === 'solo_interes' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>{d.estado}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Socios */}
      {data.socios && data.socios.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <h3 className="font-bold text-foreground">Socios Participantes</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {['Socio', 'Aporte', '% Participación', '% Interés'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.socios.map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-2 text-foreground">{s.nombre_completo || `Socio ${s.socio_id}`}</td>
                    <td className="px-4 py-2 text-foreground">{fmt(s.aporte)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{s.porcentaje?.toFixed(1)}%</td>
                    <td className="px-4 py-2 text-muted-foreground">{s.porcentaje_interes || 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagos History */}
      {data.pagos && data.pagos.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <h3 className="font-bold text-foreground">Historial de Pagos</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {['Consecutivo', 'Fecha', 'Tipo', 'Monto', 'Método', 'Referencia'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.pagos.map((pg, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-2 font-mono text-foreground">{pg.consecutivo}</td>
                    <td className="px-4 py-2 text-muted-foreground">{pg.fecha?.substring(0, 10)}</td>
                    <td className="px-4 py-2 capitalize text-foreground">{pg.tipo}</td>
                    <td className="px-4 py-2 font-medium text-emerald-400">{fmt(pg.monto_total)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{pg.metodo_pago || '-'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{pg.referencia || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPagoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pago-modal">
          <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Registrar Pago</h3>
              <button onClick={() => setShowPagoModal(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>

            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-2 block">Tipo de Pago</label>
              <div className="flex gap-3">
                {[{v:'cuota', l:'Pago de Cuotas'}, {v:'interes', l:'Solo Intereses'}, {v:'total', l:'Pago Total'}, {v:'abono_capital', l:'Abono a Capital'}].map(t => (
                  <button key={t.v} onClick={() => { setPagoTipo(t.v); setSelectedCuotas([]); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pagoTipo === t.v ? 'bg-[#D4AF37] text-black' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-testid={`pago-tipo-${t.v}`}>
                    {t.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Método de Pago</label>
                <select value={metodoPago} onChange={e => setMetodoPago(e.target.value)} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                  <option value="">Seleccionar...</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="consignacion">Consignación</option>
                </select>
              </div>
            </div>

            {pagoTipo === 'abono_capital' && (
              <div className="mb-4">
                <label className="text-sm text-muted-foreground mb-2 block">Monto del Abono a Capital</label>
                <input type="number" value={montoAbono} onChange={e => setMontoAbono(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground"
                  placeholder="Ingrese el monto..." data-testid="monto-abono-input" />
              </div>
            )}

            {pagoTipo !== 'total' && pagoTipo !== 'abono_capital' && (
              <div className="mb-4">
                <label className="text-sm text-muted-foreground mb-2 block">Seleccionar Cuotas {pagoTipo === 'interes' ? '(solo una)' : ''}</label>
                <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
                  {pendientes.map(d => (
                    <label key={d.id} className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-muted/30 ${selectedCuotas.includes(d.id) ? 'bg-[#D4AF37]/10' : ''}`}>
                      <input type={pagoTipo === 'interes' ? 'radio' : 'checkbox'} name="cuota-select" checked={selectedCuotas.includes(d.id)} onChange={() => {
                        if (pagoTipo === 'interes') setSelectedCuotas([d.id]);
                        else toggleCuota(d.id);
                      }} className="accent-[#D4AF37]" />
                      <span className="text-sm text-foreground">Cuota #{d.numero_cuota} - {d.fecha_programada}</span>
                      <span className="ml-auto text-sm font-medium text-foreground">{fmt(
                        pagoTipo === 'interes' 
                          ? (d.interes - (d.pagado_interes || 0))
                          : (d.total_cuota - (d.pagado_capital || 0) - (d.pagado_interes || 0))
                      )}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button onClick={handlePago} disabled={pagando || (pagoTipo !== 'total' && pagoTipo !== 'abono_capital' && selectedCuotas.length === 0) || (pagoTipo === 'abono_capital' && (!montoAbono || parseFloat(montoAbono) <= 0))} className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-pago-btn">
              {pagando && <Loader2 className="animate-spin" size={16} />}
              {pagoTipo === 'total' ? 'Liquidar Préstamo' : pagoTipo === 'abono_capital' ? 'Aplicar Abono a Capital' : 'Registrar Pago'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================= REPORTES RENDIMIENTO POR SOCIO =========================
const ReportesView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ax.get(`${API}/prestamos/reportes/rendimiento-socios`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    const rows = [['Socio', 'Mes', 'Capital Recibido', 'Interés Ganado', 'Total', 'Pagos']];
    data.forEach(s => {
      s.meses.forEach(m => {
        rows.push([s.nombre, m.mes, m.capital_recibido, m.interes_ganado, m.total, m.pagos_count]);
      });
      rows.push([s.nombre, 'TOTAL', s.total_capital, s.total_interes, s.total_general, '']);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rendimiento_socios_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="reportes-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Rendimiento por Socio</h2>
        {data.length > 0 && (
          <button onClick={exportCSV} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e]" data-testid="export-csv-btn">
            <Download size={18} /> Exportar CSV
          </button>
        )}
      </div>

      {data.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="mx-auto mb-4 opacity-30" size={48} />
          <p>No hay datos de rendimiento aún. Se generarán cuando se registren pagos en préstamos con socios.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.map(s => (
              <div key={s.socio_id} className="bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-shadow" data-testid={`socio-report-${s.socio_id}`}>
                <div className="text-sm text-muted-foreground mb-1">{s.nombre}</div>
                <div className="text-2xl font-bold text-foreground mb-3">{fmt(s.total_general)}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Capital: </span>
                    <span className="text-foreground font-medium">{fmt(s.total_capital)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Interés: </span>
                    <span className="text-emerald-400 font-medium">{fmt(s.total_interes)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed Table per Socio */}
          {data.map(s => (
            <div key={s.socio_id} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                <h3 className="font-bold text-foreground">{s.nombre}</h3>
                <span className="text-sm font-medium text-[#D4AF37]">Total: {fmt(s.total_general)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Mes', 'Capital Recibido', 'Interés Ganado', 'Total', '# Pagos'].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.meses.map(m => (
                      <tr key={m.mes} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-2 font-medium text-foreground">{m.mes}</td>
                        <td className="px-4 py-2 text-foreground">{fmt(m.capital_recibido)}</td>
                        <td className="px-4 py-2 text-emerald-400">{fmt(m.interes_ganado)}</td>
                        <td className="px-4 py-2 font-medium text-foreground">{fmt(m.total)}</td>
                        <td className="px-4 py-2 text-muted-foreground">{m.pagos_count}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-[#D4AF37]/30 bg-[#D4AF37]/5">
                      <td className="px-4 py-2 font-bold text-foreground">TOTAL</td>
                      <td className="px-4 py-2 font-bold text-foreground">{fmt(s.total_capital)}</td>
                      <td className="px-4 py-2 font-bold text-emerald-400">{fmt(s.total_interes)}</td>
                      <td className="px-4 py-2 font-bold text-[#D4AF37]">{fmt(s.total_general)}</td>
                      <td className="px-4 py-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ========================= REPORTES AVANZADOS VIEW =========================
const ReportesAvanzadosView = () => {
  const [subview, setSubview] = useState('menu');
  const [ganancias, setGanancias] = useState(null);
  const [sociosDetalle, setSociosDetalle] = useState(null);
  const [pagosSocios, setPagosSocios] = useState([]);
  const [portafolios, setPortafolios] = useState([]);
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInforme, setShowInforme] = useState(false);
  const [informeForm, setInformeForm] = useState({ socio_id: '', monto_pagar: '', mes: '', anio: '2026', fecha_pago: '', medio_pago: 'Transferencia', observaciones: '', utilidades: '', saldo_anterior: '', rendimiento_pct: '', deducciones: '', impuestos: '', ajustes: '', saldo_final: '' });
  const [sending, setSending] = useState(false);
  const [calcResult, setCalcResult] = useState(null);

  const loadSocios = () => ax.get(`${API}/prestamos/socios`).then(r => setSocios(r.data)).catch(() => {});
  const loadPagosSocios = () => { setLoading(true); ax.get(`${API}/prestamos/reportes/pago-socios`).then(r => setPagosSocios(r.data)).finally(() => setLoading(false)); };
  const loadPortafolios = () => { setLoading(true); ax.get(`${API}/prestamos/reportes/portafolios`).then(r => setPortafolios(r.data)).finally(() => setLoading(false)); };
  const loadGanancias = () => { setLoading(true); Promise.all([ax.get(`${API}/prestamos/reportes/ganancias-mensuales`), ax.get(`${API}/prestamos/reportes/socios-detallado`)]).then(([g, s]) => { setGanancias(g.data); setSociosDetalle(s.data); }).finally(() => setLoading(false)); };

  const handleCalcRendimientos = async () => {
    if (!informeForm.socio_id || !informeForm.mes || !informeForm.anio) return;
    try {
      const r = await ax.get(`${API}/prestamos/reportes/calcular-rendimientos`, { params: { socio_id: informeForm.socio_id, mes: informeForm.mes, anio: informeForm.anio } });
      if (r.data.error === 'ALREADY_EXISTS') { alert(r.data.message); return; }
      setCalcResult(r.data);
      setInformeForm(f => ({ ...f, utilidades: String(r.data.total_utilidades) }));
    } catch { alert('Error al calcular'); }
  };

  const handleSendInforme = async () => {
    setSending(true);
    try {
      const payload = { ...informeForm };
      ['monto_pagar', 'utilidades', 'saldo_anterior', 'rendimiento_pct', 'deducciones', 'impuestos', 'ajustes', 'saldo_final'].forEach(k => { payload[k] = parseFloat(String(payload[k]).replace(/\./g, '').replace(',', '.')) || 0; });
      const r = await ax.post(`${API}/prestamos/reportes/enviar-informe-socio`, payload);
      alert(r.data.email_sent ? 'Informe enviado por correo' : 'Informe guardado (correo no disponible)');
      setShowInforme(false); setCalcResult(null);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSending(false); }
  };

  const handleCreatePagoSocio = async () => {
    setSending(true);
    try {
      await ax.post(`${API}/prestamos/reportes/pago-socios`, {
        socio_id: informeForm.socio_id, mes: informeForm.mes, anio: parseInt(informeForm.anio) || 2026,
        fecha_emision: new Date().toISOString().split('T')[0],
        utilidades_generadas: parseFloat(informeForm.utilidades) || 0, saldo_favor_anterior: parseFloat(informeForm.saldo_anterior) || 0,
        rendimiento_mensual_porc: parseFloat(informeForm.rendimiento_pct) || 0, deducciones: parseFloat(informeForm.deducciones) || 0,
        impuestos: parseFloat(informeForm.impuestos) || 0, ajustes: parseFloat(informeForm.ajustes) || 0,
        saldo_final_mes: parseFloat(informeForm.saldo_final) || 0, fecha_pago: informeForm.fecha_pago || null,
        medio_pago: informeForm.medio_pago, valor_pagado: parseFloat(informeForm.monto_pagar) || 0,
        observaciones: informeForm.observaciones, responsable: 'Felipe Vega'
      });
      alert('Liquidación creada exitosamente');
      setShowInforme(false); setCalcResult(null); loadPagosSocios();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSending(false); }
  };

  // Informe Modal - rendered inline (NOT as component) to preserve focus
  const informeModalJSX = showInforme ? (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInforme(false)}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">Liquidar Rendimientos / Enviar Informe</h3>
          <button onClick={() => setShowInforme(false)} className="text-muted-foreground"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-muted-foreground">Socio *</label>
            <select value={informeForm.socio_id} onChange={e => setInformeForm(f => ({...f, socio_id: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-socio-select">
              <option value="">Seleccionar...</option>
              {socios.filter(s => s.estado === 'activo').map(s => <option key={s.id} value={s.id}>{s.nombre_completo}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Mes *</label>
            <select value={informeForm.mes} onChange={e => setInformeForm(f => ({...f, mes: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
              <option value="">Seleccionar...</option>
              {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Año *</label>
            <input type="number" value={informeForm.anio} onChange={e => setInformeForm(f => ({...f, anio: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
          </div>
          <div className="flex items-end">
            <button onClick={handleCalcRendimientos} disabled={!informeForm.socio_id || !informeForm.mes || !informeForm.anio} className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">Calcular Rendimientos</button>
          </div>
          {calcResult && (
            <div className="col-span-2 bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-semibold text-foreground mb-2">Utilidades calculadas: {fmt(calcResult.total_utilidades)}</p>
              {calcResult.items.map((it, i) => (
                <div key={i} className="flex justify-between text-muted-foreground text-xs"><span>{it.prestamo_consecutivo} ({it.detalle})</span><span>{fmt(it.rendimiento)}</span></div>
              ))}
            </div>
          )}
          <div>
            <label className="text-sm text-muted-foreground">Fecha de Pago</label>
            <input type="text" placeholder="2026-08-15" value={informeForm.fecha_pago} onChange={e => setInformeForm(f => ({...f, fecha_pago: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-fecha-pago" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Utilidades Generadas</label>
            <input type="text" placeholder="0" value={informeForm.utilidades} onChange={e => setInformeForm(f => ({...f, utilidades: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-utilidades" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Saldo a Favor Anterior</label>
            <input type="text" placeholder="0" value={informeForm.saldo_anterior} onChange={e => setInformeForm(f => ({...f, saldo_anterior: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-saldo-anterior" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Rendimiento %</label>
            <input type="text" placeholder="0" value={informeForm.rendimiento_pct} onChange={e => setInformeForm(f => ({...f, rendimiento_pct: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-rendimiento" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Deducciones</label>
            <input type="text" placeholder="0" value={informeForm.deducciones} onChange={e => setInformeForm(f => ({...f, deducciones: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-deducciones" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Impuestos</label>
            <input type="text" placeholder="0" value={informeForm.impuestos} onChange={e => setInformeForm(f => ({...f, impuestos: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-impuestos" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Ajustes</label>
            <input type="text" placeholder="0" value={informeForm.ajustes} onChange={e => setInformeForm(f => ({...f, ajustes: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-ajustes" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Saldo Final Mes</label>
            <input type="text" placeholder="0" value={informeForm.saldo_final} onChange={e => setInformeForm(f => ({...f, saldo_final: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-saldo-final" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Valor Pagado *</label>
            <input type="text" placeholder="0" value={informeForm.monto_pagar} onChange={e => setInformeForm(f => ({...f, monto_pagar: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="informe-valor-pagado" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Medio de Pago</label>
            <select value={informeForm.medio_pago} onChange={e => setInformeForm(f => ({...f, medio_pago: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">{['Transferencia','Efectivo','Nequi','Otro'].map(m => <option key={m}>{m}</option>)}</select>
          </div>
          <div className="col-span-2">
            <label className="text-sm text-muted-foreground">Observaciones</label>
            <textarea value={informeForm.observaciones} onChange={e => setInformeForm(f => ({...f, observaciones: e.target.value}))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={handleCreatePagoSocio} disabled={sending || !informeForm.socio_id || !informeForm.mes} className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-liquidacion-btn">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Guardar Liquidación
          </button>
          <button onClick={handleSendInforme} disabled={sending || !informeForm.socio_id} className="flex-1 bg-[#D4AF37] text-black py-3 rounded-lg font-medium hover:bg-[#c29d2e] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-send-informe">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Guardar y Enviar Email
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // Menu view (4 cards like PHP)
  if (subview === 'menu') return (
    <div data-testid="reportes-avanzados">
      <h2 className="text-2xl font-bold text-foreground mb-2">Módulo de Reportes</h2>
      <p className="text-muted-foreground text-sm mb-6">Seleccione el tipo de reporte que desea generar o consultar.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <div className="bg-card border-l-4 border-blue-500 border border-border rounded-xl p-5" data-testid="report-card-rendimientos">
          <div className="w-11 h-11 bg-blue-500/10 rounded-xl flex items-center justify-center mb-3"><CreditCard size={22} className="text-blue-400" /></div>
          <h3 className="text-base font-bold text-foreground mb-1.5">Rendimientos Mensuales</h3>
          <p className="text-muted-foreground text-xs mb-4 leading-relaxed">Liquidaciones mensuales, historial de pagos y reportes PDF para socios.</p>
          <div className="space-y-2">
            <button onClick={() => { loadSocios(); setShowInforme(true); }} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-blue-700 text-xs" data-testid="liquidar-rendimientos-btn"><PlusCircle size={14} /> Liquidar Rendimientos</button>
            <button onClick={() => { loadPagosSocios(); setSubview('pagados'); }} className="w-full flex items-center justify-center gap-2 border border-blue-500/50 text-blue-400 px-3 py-2 rounded-lg font-medium hover:bg-blue-500/10 text-xs" data-testid="rendimientos-pagados-btn"><BarChart3 size={14} /> Rendimientos pagados</button>
          </div>
        </div>
        <div className="bg-card border-l-4 border-cyan-500 border border-border rounded-xl p-5" data-testid="report-card-portafolios">
          <div className="w-11 h-11 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-3"><Briefcase size={22} className="text-cyan-400" /></div>
          <h3 className="text-base font-bold text-foreground mb-1.5">Portafolios de Inversión</h3>
          <p className="text-muted-foreground text-xs mb-4 leading-relaxed">Resumen de aportes activos y capital total invertido por cada socio.</p>
          <button onClick={() => { loadPortafolios(); setSubview('portafolios'); }} className="w-full flex items-center justify-center gap-2 bg-cyan-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-cyan-700 text-xs" data-testid="ver-portafolios-btn"><FileText size={14} /> Ver Portafolios</button>
        </div>
        <div className="bg-card border-l-4 border-emerald-500 border border-border rounded-xl p-5" data-testid="report-card-general">
          <div className="w-11 h-11 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-3"><BarChart3 size={22} className="text-emerald-400" /></div>
          <h3 className="text-base font-bold text-foreground mb-1.5">Reporte General</h3>
          <p className="text-muted-foreground text-xs mb-4 leading-relaxed">Listado detallado de préstamos generados con filtrado por fechas.</p>
          <button onClick={() => { loadGanancias(); setSubview('general'); }} className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-emerald-700 text-xs" data-testid="filtrar-fechas-btn"><Calendar size={14} /> Filtrar por Fechas</button>
        </div>
        <div className="bg-card border-l-4 border-yellow-500 border border-border rounded-xl p-5" data-testid="report-card-pagos">
          <div className="w-11 h-11 bg-yellow-500/10 rounded-xl flex items-center justify-center mb-3"><DollarSign size={22} className="text-yellow-400" /></div>
          <h3 className="text-base font-bold text-foreground mb-1.5">Reporte de Pagos</h3>
          <p className="text-muted-foreground text-xs mb-4 leading-relaxed">Consolidado de pagos y recaudos filtrado por rango de fechas.</p>
          <button onClick={() => { loadGanancias(); setSubview('pagos'); }} className="w-full flex items-center justify-center gap-2 bg-yellow-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-yellow-700 text-xs" data-testid="filtrar-pagos-btn"><Calendar size={14} /> Filtrar Pagos</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <a href={`${API}/prestamos/reportes/pdf/general`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-muted text-foreground px-4 py-2 rounded-lg font-medium hover:bg-muted/80 text-sm" data-testid="download-general-pdf"><Download size={16} /> PDF General</a>
        <a href={`${API}/prestamos/reportes/zip/comprobantes`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-muted text-foreground px-4 py-2 rounded-lg font-medium hover:bg-muted/80 text-sm" data-testid="download-zip"><Download size={16} /> ZIP Comprobantes</a>
        <button onClick={() => { loadSocios(); setShowInforme(true); }} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e] text-sm" data-testid="send-informe-btn"><Mail size={16} /> Enviar Informe a Socio</button>
      </div>
      {informeModalJSX}
    </div>
  );

  const BackBtn = () => <button onClick={() => setSubview('menu')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 text-sm"><ChevronLeft size={16} /> Volver a Reportes</button>;

  // Rendimientos Pagados
  if (subview === 'pagados') return (
    <div data-testid="rendimientos-pagados-view">
      <BackBtn />
      <h2 className="text-xl font-bold text-foreground mb-4">Rendimientos Pagados</h2>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div> : (
        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm"><thead className="bg-muted/50"><tr>{['Consecutivo','Socio','Mes/Año','Utilidades','Deducciones','Saldo Final','Pagado','Medio','Acciones'].map(h => <th key={h} className="px-3 py-2 text-left text-muted-foreground font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>{pagosSocios.map(p => (
              <tr key={p.id} className="border-t border-border hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-[#D4AF37]">{p.consecutivo}</td>
                <td className="px-3 py-2 text-foreground">{p.socio_nombre}</td>
                <td className="px-3 py-2">{p.mes} {p.anio}</td>
                <td className="px-3 py-2 text-emerald-400">{fmt(p.utilidades_generadas)}</td>
                <td className="px-3 py-2 text-red-400">{fmt(p.deducciones)}</td>
                <td className="px-3 py-2 font-semibold">{fmt(p.saldo_final_mes)}</td>
                <td className="px-3 py-2">{fmt(p.valor_pagado)}</td>
                <td className="px-3 py-2 text-muted-foreground">{p.medio_pago}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <a href={`${API}/prestamos/reportes/pdf/pago-socio/${p.id}`} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#c29d2e]" title="Descargar PDF" data-testid={`pdf-pago-${p.id}`}><Download size={14} /></a>
                    <button onClick={async () => { try { const r = await ax.post(`${API}/prestamos/reportes/email/pago-socio/${p.id}`); alert(r.data.email_sent ? `Email enviado a ${r.data.to}` : r.data.message); } catch(e) { alert(e.response?.data?.detail || 'Error'); }}} className="text-blue-400 hover:text-blue-300" title="Enviar por Email" data-testid={`email-pago-${p.id}`}><Mail size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}{pagosSocios.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No hay rendimientos pagados</td></tr>}</tbody>
          </table>
        </div>
      )}
      {informeModalJSX}
    </div>
  );

  // Portafolios
  if (subview === 'portafolios') return (
    <div data-testid="portafolios-view">
      <BackBtn />
      <h2 className="text-xl font-bold text-foreground mb-4">Portafolios de Inversión</h2>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div> : (
        <div className="space-y-4">{portafolios.map(s => (
          <div key={s.id} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div><h3 className="font-bold text-foreground">{s.nombre_completo}</h3><p className="text-sm text-muted-foreground">{s.documento} • {s.email}</p></div>
              <div className="text-right"><p className="text-lg font-bold text-[#D4AF37]">{fmt(s.total_aportado)}</p><p className="text-xs text-muted-foreground">{s.inversiones_count} inversiones</p></div>
            </div>
            {s.inversiones.length > 0 && (
              <table className="w-full text-xs"><thead><tr className="text-muted-foreground">{['Préstamo','Aporte','Tasa','Rend. Est.','Estado','Inicio'].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
                <tbody>{s.inversiones.map((inv, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="px-2 py-1 font-mono text-[#D4AF37]">{inv.consecutivo}</td><td className="px-2 py-1">{fmt(inv.aporte)}</td>
                    <td className="px-2 py-1">{inv.tasa}%</td><td className="px-2 py-1 text-emerald-400">{fmt(inv.rendimiento_estimado)}</td>
                    <td className="px-2 py-1 capitalize">{inv.estado}</td><td className="px-2 py-1 text-muted-foreground">{inv.fecha_inicio}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            <a href={`${API}/prestamos/reportes/pdf/socio/${s.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline mt-2"><Download size={12} /> PDF</a>
          </div>
        ))}</div>
      )}
    </div>
  );

  // General / Pagos
  if (subview === 'general' || subview === 'pagos') return (
    <div data-testid={`reporte-${subview}-view`}>
      <BackBtn />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground">{subview === 'general' ? 'Reporte General' : 'Reporte de Pagos'}</h2>
        <a href={`${API}/prestamos/reportes/pdf/general`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-muted text-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-muted/80"><Download size={14} /> PDF</a>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div> : ganancias && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">Deuda Activa</div><div className="text-lg font-bold text-red-400">{fmt(ganancias.resumen.total_deuda_activa)}</div></div>
            <div className="bg-card border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">Capital Pendiente</div><div className="text-lg font-bold text-foreground">{fmt(ganancias.resumen.capital_pendiente)}</div></div>
            <div className="bg-card border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">Inversión Socios</div><div className="text-lg font-bold text-[#D4AF37]">{fmt(ganancias.resumen.total_inversion_socios)}</div></div>
            <div className="bg-card border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">Disponible</div><div className="text-lg font-bold text-emerald-400">{fmt(ganancias.resumen.disponible)}</div></div>
          </div>
          {subview === 'general' && sociosDetalle && <SociosDetalleTable data={sociosDetalle} />}
          {subview === 'pagos' && (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm"><thead className="bg-muted/50"><tr>{['Mes','Capital','Interés','Total','Pagos','Rendimiento %'].map(h => <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium">{h}</th>)}</tr></thead>
                <tbody>{ganancias.meses.map(m => (
                  <tr key={m.mes} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{m.mes}</td><td className="px-4 py-2">{fmt(m.capital_recibido)}</td>
                    <td className="px-4 py-2 text-emerald-400">{fmt(m.interes_ganado)}</td><td className="px-4 py-2 font-bold">{fmt(m.total)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{m.pagos}</td><td className="px-4 py-2 text-[#D4AF37]">{m.rendimiento_pct}%</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return null;
};

// Socios Detail Table with subtotals and expandable detail
const SociosDetalleTable = ({ data }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(p => ({...p, [id]: !p[id]}));

  const exportCSV = () => {
    const rows = [['Socio','Documento','Aporte','Portafolio','Interés Mensual','Estado']];
    data.socios.forEach(s => rows.push([s.nombre, s.documento, s.aporte_total, s.portfolio_type, s.total_interes_mensual, s.estado]));
    rows.push(['TOTAL','',data.totales.total_aportes,'',data.totales.total_interes_mensual,'']);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'socios_detalle.csv'; a.click();
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={exportCSV} className="flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#c29d2e]"><Download size={16} /> Descargar CSV</button>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>{['','Socio','Documento','Aporte','Portafolio','Interés Mensual','Estado'].map(h => <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.socios.map(s => (
              <React.Fragment key={s.id}>
                <tr className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => toggle(s.id)}>
                  <td className="px-4 py-2">{expanded[s.id] ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}</td>
                  <td className="px-4 py-2 font-medium text-foreground">{s.nombre}</td>
                  <td className="px-4 py-2 text-muted-foreground">{s.documento}</td>
                  <td className="px-4 py-2 text-foreground">{fmt(s.aporte_total)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.portfolio_type==='bajo'?'bg-emerald-500/20 text-emerald-400':s.portfolio_type==='alto'?'bg-red-500/20 text-red-400':'bg-yellow-500/20 text-yellow-400'}`}>{s.portfolio_type || 'medio'}</span></td>
                  <td className="px-4 py-2 text-emerald-400 font-medium">{fmt(s.total_interes_mensual)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${s.estado==='activo'?'bg-emerald-500/20 text-emerald-400':'bg-red-500/20 text-red-400'}`}>{s.estado}</span></td>
                </tr>
                {expanded[s.id] && s.deudas.length > 0 && (
                  <tr><td colSpan={7} className="px-8 py-3 bg-muted/20">
                    <table className="w-full text-xs">
                      <thead><tr className="text-muted-foreground">{['Préstamo','Deudor','Aporte','%','Interés %','Interés Mensual','Estado'].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
                      <tbody>{s.deudas.map((d,i) => (
                        <tr key={i} className="border-t border-border/30">
                          <td className="px-2 py-1 font-mono text-[#D4AF37]">{d.consecutivo}</td>
                          <td className="px-2 py-1 text-foreground">{d.deudor}</td>
                          <td className="px-2 py-1">{fmt(d.aporte)}</td>
                          <td className="px-2 py-1">{d.porcentaje?.toFixed(1)}%</td>
                          <td className="px-2 py-1">{d.interes_pct}%</td>
                          <td className="px-2 py-1 text-emerald-400">{fmt(d.interes_mensual)}</td>
                          <td className="px-2 py-1 capitalize">{d.estado}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="bg-[#D4AF37]/10 border-t-2 border-[#D4AF37]/30">
            <tr>
              <td></td><td className="px-4 py-2 font-bold text-foreground">TOTAL</td><td></td>
              <td className="px-4 py-2 font-bold text-foreground">{fmt(data.totales.total_aportes)}</td><td></td>
              <td className="px-4 py-2 font-bold text-emerald-400">{fmt(data.totales.total_interes_mensual)}</td><td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ========================= PRESTAMOS POR DEUDOR VIEW =========================
const PrestamosPorDeudorView = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    ax.get(`${API}/prestamos/reportes/prestamos-por-deudor`)
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(p => ({...p, [id]: !p[id]}));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="prestamos-por-deudor">
      <h2 className="text-2xl font-bold mb-6 text-foreground">Préstamos por Deudor</h2>
      <div className="space-y-4">
        {(data?.deudores || []).map(d => (
          <div key={d.deudor_id} className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30" onClick={() => toggle(d.deudor_id)}>
              <div className="flex items-center gap-3">
                {expanded[d.deudor_id] ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                <div>
                  <span className="font-medium text-foreground">{d.nombre}</span>
                  <span className="ml-2 text-sm text-muted-foreground">{d.documento}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">{d.num_prestamos} préstamo{d.num_prestamos > 1 ? 's' : ''}</span>
                <span className="font-bold text-foreground">{fmt(d.total_deuda)}</span>
              </div>
            </div>
            {expanded[d.deudor_id] && (
              <div className="border-t border-border">
                {d.prestamos.map(p => (
                  <div key={p.id} className="border-b border-border/50 last:border-0">
                    <div className="px-4 py-3 bg-muted/20 flex items-center justify-between">
                      <div>
                        <span className="font-mono text-[#D4AF37] font-bold">{p.consecutivo}</span>
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${p.estado==='activo'?'bg-emerald-500/20 text-emerald-400':'bg-blue-500/20 text-blue-400'}`}>{p.estado}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-foreground">{fmt(p.monto)}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{p.tasa_interes}% — {p.plazo} cuotas</span>
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>{['#','Fecha','Capital','Interés','Total','Pagado Cap.','Pagado Int.','Saldo','Estado'].map(h => <th key={h} className="px-2 py-1.5 text-left text-muted-foreground">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {(p.detalles || []).map(det => (
                          <tr key={det.id} className={`border-t border-border/30 ${det.estado==='pagado'?'bg-emerald-500/5':''}`}>
                            <td className="px-2 py-1">{det.numero_cuota}</td>
                            <td className="px-2 py-1 text-muted-foreground">{det.fecha_programada}</td>
                            <td className="px-2 py-1">{fmt(det.capital)}</td>
                            <td className="px-2 py-1">{fmt(det.interes)}</td>
                            <td className="px-2 py-1 font-medium">{fmt(det.total_cuota)}</td>
                            <td className="px-2 py-1 text-emerald-400">{fmt(det.pagado_capital)}</td>
                            <td className="px-2 py-1 text-emerald-400">{fmt(det.pagado_interes)}</td>
                            <td className="px-2 py-1 text-muted-foreground">{fmt(det.saldo_restante)}</td>
                            <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[10px] ${det.estado==='pagado'?'bg-emerald-500/20 text-emerald-400':'bg-gray-500/20 text-gray-400'}`}>{det.estado}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {data && (
          <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 rounded-xl p-4 flex justify-between items-center">
            <span className="font-bold text-foreground text-lg">Total General</span>
            <span className="font-bold text-[#D4AF37] text-xl">{fmt(data.grand_total)}</span>
          </div>
        )}
      </div>
    </div>
  );
};



// ========================= PRESTAMOS POR SOCIO VIEW =========================
const PrestamosPorSocioView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    ax.get(`${API}/prestamos/reportes/socios-detallado`)
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(p => ({...p, [id]: !p[id]}));

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Socio','Documento','Aporte Total','Portafolio','Préstamo','Deudor','Aporte Deuda','% Interes','Int. Mensual','Estado']];
    data.socios.forEach(s => {
      if (s.deudas.length === 0) {
        rows.push([s.nombre, s.documento, s.aporte_total, s.portfolio_type, '-','-','-','-','-', s.estado]);
      } else {
        s.deudas.forEach(d => {
          rows.push([s.nombre, s.documento, s.aporte_total, s.portfolio_type, d.consecutivo, d.deudor, d.aporte, d.interes_pct, d.interes_mensual, d.estado]);
        });
      }
    });
    rows.push(['TOTAL','','','','','',data.totales.total_aportes,'',data.totales.total_interes_mensual,'']);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'prestamos_por_socio.csv'; a.click();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="prestamos-por-socio">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Préstamos por Socio</h2>
        <button onClick={exportCSV} className="flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#c29d2e]"><Download size={16} /> Descargar CSV</button>
      </div>
      <div className="space-y-4">
        {(data?.socios || []).map(s => (
          <div key={s.id} className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30" onClick={() => toggle(s.id)}>
              <div className="flex items-center gap-3">
                {expanded[s.id] ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                <div>
                  <span className="font-medium text-foreground">{s.nombre}</span>
                  <span className="ml-2 text-sm text-muted-foreground">{s.documento}</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${s.portfolio_type==='bajo'?'bg-emerald-500/20 text-emerald-400':s.portfolio_type==='alto'?'bg-red-500/20 text-red-400':'bg-yellow-500/20 text-yellow-400'}`}>{s.portfolio_type || 'medio'}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">{s.deudas.length} deuda{s.deudas.length !== 1 ? 's' : ''}</span>
                <div className="text-right">
                  <div className="font-bold text-foreground">{fmt(s.aporte_total)}</div>
                  <div className="text-xs text-emerald-400">+{fmt(s.total_interes_mensual)}/mes</div>
                </div>
              </div>
            </div>
            {expanded[s.id] && s.deudas.length > 0 && (
              <div className="border-t border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>{['Préstamo','Deudor','Aporte','% Part.','% Interés','Int. Mensual','Estado'].map(h => <th key={h} className="px-4 py-2 text-left text-muted-foreground font-medium text-xs">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {s.deudas.map((d, i) => (
                      <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                        <td className="px-4 py-2 font-mono text-[#D4AF37]">{d.consecutivo}</td>
                        <td className="px-4 py-2 text-foreground">{d.deudor}</td>
                        <td className="px-4 py-2 text-foreground">{fmt(d.aporte)}</td>
                        <td className="px-4 py-2 text-muted-foreground">{d.porcentaje?.toFixed(1)}%</td>
                        <td className="px-4 py-2 text-muted-foreground">{d.interes_pct}%</td>
                        <td className="px-4 py-2 text-emerald-400 font-medium">{fmt(d.interes_mensual)}</td>
                        <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${d.estado==='activo'?'bg-emerald-500/20 text-emerald-400':'bg-blue-500/20 text-blue-400'}`}>{d.estado}</span></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#D4AF37]/5">
                    <tr>
                      <td colSpan={2} className="px-4 py-2 font-bold text-foreground text-xs">Subtotal</td>
                      <td className="px-4 py-2 font-bold text-foreground text-xs">{fmt(s.deudas.reduce((a, d) => a + (d.aporte || 0), 0))}</td>
                      <td colSpan={2}></td>
                      <td className="px-4 py-2 font-bold text-emerald-400 text-xs">{fmt(s.total_interes_mensual)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        ))}
        {data && (
          <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 rounded-xl p-4 flex justify-between items-center">
            <span className="font-bold text-foreground text-lg">Total General</span>
            <div className="text-right">
              <div className="font-bold text-[#D4AF37] text-xl">{fmt(data.totales.total_aportes)}</div>
              <div className="text-sm text-emerald-400">+{fmt(data.totales.total_interes_mensual)}/mes</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ========================= NOTIFICACIONES VIEW =========================
const NotificacionesView = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await ax.get(`${API}/admin/pending-users`); setPendingUsers(r.data); } catch(e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (userId, roles) => {
    try {
      await ax.post(`${API}/admin/approve-user/${userId}`, { roles });
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDeny = async (userId) => {
    if (!window.confirm('¿Denegar acceso a este usuario?')) return;
    try { await ax.post(`${API}/admin/deny-user/${userId}`); load(); } catch(e) { alert('Error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>;

  return (
    <div data-testid="notificaciones-view">
      <h2 className="text-2xl font-bold mb-6 text-foreground">Notificaciones</h2>
      {pendingUsers.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <p className="text-muted-foreground">No hay solicitudes pendientes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingUsers.map(u => (
            <div key={u.user_id} className="bg-card border border-border rounded-xl p-5" data-testid={`pending-user-${u.user_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {u.picture && <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />}
                  <div>
                    <p className="font-medium text-foreground">{u.name}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                    <p className="text-xs text-muted-foreground">Registrado: {new Date(u.created_at).toLocaleDateString('es-CO')}</p>
                  </div>
                </div>
                <button onClick={() => handleDeny(u.user_id)} className="text-red-400 hover:text-red-300 text-sm px-3 py-1 rounded border border-red-400/30 hover:bg-red-500/10" data-testid={`deny-${u.user_id}`}>
                  Denegar
                </button>
              </div>

              {/* Deudor application details */}
              {u.deudor_application && (
                <div className="bg-muted/30 rounded-lg p-4 mb-4 border border-border">
                  <h4 className="text-sm font-bold text-foreground mb-2">Solicitud de Préstamo</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Nombre:</span> <span className="text-foreground">{u.deudor_application.nombre_completo}</span></div>
                    <div><span className="text-muted-foreground">Documento:</span> <span className="text-foreground">{u.deudor_application.documento}</span></div>
                    <div><span className="text-muted-foreground">Teléfono:</span> <span className="text-foreground">{u.deudor_application.telefono}</span></div>
                    <div><span className="text-muted-foreground">Dirección:</span> <span className="text-foreground">{u.deudor_application.direccion}</span></div>
                    <div><span className="text-muted-foreground">Monto deseado:</span> <span className="text-foreground font-medium">{fmt(u.deudor_application.monto_deseado)}</span></div>
                    <div><span className="text-muted-foreground">Plazo:</span> <span className="text-foreground">{u.deudor_application.meses_plazo} meses</span></div>
                    <div><span className="text-muted-foreground">Ingresos:</span> <span className="text-foreground">{fmt(u.deudor_application.ingresos_totales)}</span></div>
                    <div><span className="text-muted-foreground">Contrato:</span> <span className="text-foreground capitalize">{u.deudor_application.tipo_contrato}</span></div>
                  </div>
                </div>
              )}

              {/* Approval buttons */}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => handleApprove(u.user_id, ['normal'])}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-500" data-testid={`approve-normal-${u.user_id}`}>
                  Aprobar como Normal
                </button>
                <button onClick={() => handleApprove(u.user_id, ['deudor'])}
                  className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-500" data-testid={`approve-deudor-${u.user_id}`}>
                  Aprobar como Deudor
                </button>
                <button onClick={() => handleApprove(u.user_id, ['inversionista'])}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-500" data-testid={`approve-investor-${u.user_id}`}>
                  Aprobar como Inversionista
                </button>
                <button onClick={() => handleApprove(u.user_id, ['normal', 'deudor'])}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-500">
                  Normal + Deudor
                </button>
                <button onClick={() => handleApprove(u.user_id, ['normal', 'inversionista'])}
                  className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-500">
                  Normal + Inversionista
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================= MAIN PRESTAMOS PAGE =========================
export default function Prestamos() {
  const { user } = useAuth();
  const [view, setView] = useState('dashboard');
  const [viewParam, setViewParam] = useState(null);
  const [metrics, setMetrics] = useState({ acreedores_activos: 0, prestamos_totales: 0, abonos_mes: 0, socios_activos: 0 });

  useEffect(() => {
    ax.get(`${API}/prestamos/dashboard`).then(r => setMetrics(r.data)).catch(() => {});
  }, [view]);

  const navigate = (v, param = null) => { setView(v); setViewParam(param); };

  if (!user?.is_admin && !user?.is_admin_holdings) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="mx-auto mb-4 text-yellow-400" size={48} />
          <h2 className="text-xl font-bold text-foreground mb-2">Acceso Restringido</h2>
          <p className="text-muted-foreground">Solo los administradores pueden acceder al módulo de préstamos.</p>
        </div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Building2 },
    { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
    { id: 'socios', label: 'Socios', icon: Users },
    { id: 'deudores', label: 'Deudores', icon: UserCheck },
    { id: 'prestamos', label: 'Préstamos', icon: Landmark },
    { id: 'por-deudor', label: 'Por Deudor', icon: CreditCard },
    { id: 'por-socio', label: 'Por Socio', icon: Briefcase },
    { id: 'reportes', label: 'Reportes', icon: BarChart3 },
    { id: 'blog', label: 'Blog', icon: BookOpen },
  ];

  return (
    <div className="flex gap-0 min-h-[calc(100vh-80px)]" data-testid="prestamos-module">
      {/* Sidebar */}
      <div className="w-56 shrink-0 bg-card border-r border-border" data-testid="prestamos-sidebar">
        <div className="p-4 border-b border-border">
          <h3 className="font-bold text-foreground flex items-center gap-2"><Landmark size={18} className="text-[#D4AF37]" /> Administración</h3>
        </div>
        <nav className="p-2">
          {sidebarItems.map(item => (
            <button key={item.id} onClick={() => navigate(item.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 ${view === item.id || (view === 'detalle-prestamo' && item.id === 'prestamos') || (view === 'nuevo-prestamo' && item.id === 'prestamos') ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`} data-testid={`sidebar-${item.id}`}>
              <item.icon size={18} /> {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        {view === 'dashboard' && <DashboardView metrics={metrics} onNavigate={navigate} />}
        {view === 'notificaciones' && <NotificacionesView />}
        {view === 'socios' && <SociosView onNavigate={navigate} />}
        {view === 'deudores' && <DeudoresView onNavigate={navigate} />}
        {view === 'prestamos' && <PrestamosListView onNavigate={navigate} />}
        {view === 'nuevo-prestamo' && <NuevoPrestamoForm onNavigate={navigate} />}
        {view === 'detalle-prestamo' && viewParam && <PrestamoDetailView prestamoId={viewParam} onNavigate={navigate} />}
        {view === 'por-deudor' && <PrestamosPorDeudorView onNavigate={navigate} />}
        {view === 'por-socio' && <PrestamosPorSocioView />}
        {view === 'reportes' && <ReportesAvanzadosView />}
        {view === 'blog' && <BlogAdmin onBack={() => navigate('dashboard')} />}
      </div>
    </div>
  );
}
